import {
  CompanySettings,
  GstBacklog,
  GstChecks,
  GstZohoRegistration,
} from "../../domain/entities/CompanySettings";
import {
  CompanySettingsDTO,
  GstBacklogDTO,
  GstChecksDTO,
  GstZohoRegistrationDTO,
} from "../models/CompanySettingsDTO";

/**
 * Wire shape to domain shape.
 *
 * The maps (`values`, `locked`, `options`) are copied rather than renamed: their
 * keys are database field names shared with the server, and translating them
 * would mean maintaining the same list in two languages.
 */
export class CompanySettingsMapper {
  static toDomain(dto: CompanySettingsDTO): CompanySettings {
    return {
      companyId: dto.company_id,
      values: { ...dto.values },
      locked: { ...dto.locked },
      options: { ...dto.options },
      updatedAt: dto.updated_at ?? null,
      secretSet: dto.secret_set,
      // ZOUR-19. Left undefined when the server did not send the block, so the
      // panel can tell "not looked at" apart from "looked at, unreachable".
      zoho: dto.zoho ? CompanySettingsMapper.toZoho(dto.zoho) : undefined,
      backlog: dto.backlog ? CompanySettingsMapper.toBacklog(dto.backlog) : undefined,
      warnings: dto.warnings ?? undefined,
    };
  }

  static toZoho(dto: GstZohoRegistrationDTO): GstZohoRegistration {
    return {
      reachable: dto.reachable,
      registered: dto.registered,
      gstin: dto.gstin,
      registeredOn: dto.registered_on,
      organizationId: dto.organization_id,
      error: dto.error,
    };
  }

  static toBacklog(dto: GstBacklogDTO): GstBacklog {
    return {
      invoices: dto.invoices,
      bills: dto.bills,
      total: dto.total,
    };
  }

  static toChecks(dto: GstChecksDTO): GstChecks {
    return {
      pastDocuments: {
        checked: dto.past_documents.checked,
        gstEnabledFrom: dto.past_documents.gst_enabled_from,
        documents: dto.past_documents.documents.map((doc) => ({
          kind: doc.kind,
          id: doc.id,
          documentNumber: doc.document_number,
          date: doc.date,
        })),
        truncated: dto.past_documents.truncated,
        message: dto.past_documents.message,
      },
      contacts: {
        contacts: dto.contacts.contacts.map((contact) => ({
          kind: contact.kind,
          id: contact.id,
          name: contact.name,
        })),
        truncated: dto.contacts.truncated,
        message: dto.contacts.message,
      },
      zohoContacts: dto.zoho_contacts
        ? {
            checked: dto.zoho_contacts.checked,
            contacts: dto.zoho_contacts.contacts.map((contact) => ({
              kind: contact.kind,
              id: contact.id,
              name: contact.name,
              localTreatment: contact.local_treatment,
            })),
            truncated: dto.zoho_contacts.truncated,
            message: dto.zoho_contacts.message,
          }
        : undefined,
    };
  }
}
